import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react';

interface FieldProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  hint?: ReactNode;
  /** Shown under the field in red, in place of the hint, and announced with it */
  error?: ReactNode;
}

/**
 * A text input with a visible, associated label, an optional hint, and an error that replaces the
 * hint while it applies (aria-invalid, announced through aria-describedby).
 */
export function TextField({ label, hint, error, id, ...input }: FieldProps) {
  const generated = useId();
  const inputId = id ?? generated;
  const note = error || hint;
  const noteId = note ? `${inputId}-hint` : undefined;
  return (
    <div className="field">
      <label htmlFor={inputId}>{label}</label>
      <input id={inputId} aria-describedby={noteId} aria-invalid={error ? true : undefined} {...input} />
      {note ? (
        <small id={noteId} className={error ? 'field-hint text-destructive' : 'field-hint'}>
          {note}
        </small>
      ) : null}
    </div>
  );
}

/** A date picker (native, themed): value "YYYY-MM-DD" */
export function DateField(props: Omit<FieldProps, 'type'>) {
  return <TextField type="date" {...props} />;
}

/** A time picker (native, themed): value "HH:MM"; `step={900}` for 15-minute steps */
export function TimeField(props: Omit<FieldProps, 'type'>) {
  return <TextField type="time" {...props} />;
}

/** A date and time picker (native, themed): value "YYYY-MM-DDTHH:MM", in the viewer's zone */
export function DateTimeField(props: Omit<FieldProps, 'type'>) {
  return <TextField type="datetime-local" {...props} />;
}

interface TextAreaFieldProps extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  hint?: ReactNode;
  error?: ReactNode;
}

export function TextAreaField({ label, hint, error, id, ...textarea }: TextAreaFieldProps) {
  const generated = useId();
  const fieldId = id ?? generated;
  const note = error || hint;
  const noteId = note ? `${fieldId}-hint` : undefined;
  return (
    <div className="field">
      <label htmlFor={fieldId}>{label}</label>
      <textarea id={fieldId} aria-describedby={noteId} aria-invalid={error ? true : undefined} {...textarea} />
      {note ? (
        <small id={noteId} className={error ? 'field-hint text-destructive' : 'field-hint'}>
          {note}
        </small>
      ) : null}
    </div>
  );
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  children: ReactNode;
}

export function SelectField({ label, id, children, ...select }: SelectFieldProps) {
  const generated = useId();
  const selectId = id ?? generated;
  return (
    <div className="field">
      <label htmlFor={selectId}>{label}</label>
      <select id={selectId} {...select}>
        {children}
      </select>
    </div>
  );
}

export function CheckboxField({ label, ...input }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & { label: string }) {
  return (
    <label className="checkbox">
      <input type="checkbox" {...input} />
      <span>{label}</span>
    </label>
  );
}
